import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';
import { SpaceCard } from '../ui/Card';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useCredits } from '../../hooks/useCredits';
import { MixedText } from '../../utils/rtl';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const { 
    createSubscriptionCheckout,
    subscriptionPlans,
    loading: plansLoading
  } = useCredits();
  
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubscriptionPurchase = async (planId: string) => {
    try {
      setProcessingPlan(planId);
      const checkoutUrl = await createSubscriptionCheckout(planId);
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Failed to create subscription checkout:', error);
      setProcessingPlan(null);
    }
  };

  // Show loading spinner if plans are loading
  if (plansLoading || subscriptionPlans.length === 0) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-[60] p-4 pt-20" 
           style={{ background: 'rgba(26, 15, 43, 0.8)' }}>
        <SpaceCard constellation="cassiopeia" className="p-8">
          <div className="flex items-center justify-center">
            <LoadingSpinner size="lg" className="text-vibrant-orange" />
            <span className="ml-3 text-surface-primary">
              <MixedText>{isRTL ? 'טוען תמחור...' : 'Loading pricing...'}</MixedText>
            </span>
          </div>
        </SpaceCard>
      </div>
    );
  }

  // Find free and premium plans
  const freePlan = subscriptionPlans.find(plan => plan.id === 'free');
  const premiumPlan = subscriptionPlans.find(plan => plan.id === 'premium');

  // Component to render a plan card
  const PlanCard = ({ plan, className = "" }: { plan: any; className?: string }) => {
    const isPremium = plan.id === 'premium';
    const isCurrent = plan.is_current;
    
    return (
      <div className={`relative group ${className}`}>
        {/* Featured Badge for Premium */}
        {isPremium && (
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
            <div className="bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90 text-white px-6 py-2 rounded-full text-sm font-bold font-hebrew shadow-lg">
              <MixedText>{isRTL ? 'הכי פופולרי' : 'Most Popular'}</MixedText>
            </div>
          </div>
        )}
        
        <SpaceCard 
          constellation={isPremium ? "cassiopeia" : "orion"}
          className={`h-full ${isPremium 
            ? 'border-2 border-vibrant-orange/50 bg-gradient-to-br from-vibrant-orange/5 to-vibrant-orange/10 hover:border-vibrant-orange/70 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-vibrant-orange/20'
            : 'border border-surface-muted/20 hover:border-surface-muted/40 transition-all duration-300'
          }`}
        >
          <div className="p-4 md:p-6 lg:p-8">
            {/* Plan Header */}
            <div className="text-center mb-4 md:mb-6 lg:mb-8">
              <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-surface-primary font-hebrew mb-2 md:mb-3 text-center">
                <MixedText>{plan.name}</MixedText>
              </h3>
              <div className={`${plan.price_ils === 0 ? 'text-2xl md:text-3xl lg:text-4xl' : 'flex items-baseline justify-center gap-1'}`}>
                {plan.price_ils === 0 ? (
                  <div className="font-bold text-surface-primary font-hebrew">
                    <MixedText>{isRTL ? 'חינם' : 'Free'}</MixedText>
                  </div>
                ) : (
                  <>
                    <span className="text-sm md:text-base lg:text-lg text-surface-secondary font-hebrew">
                      {isRTL ? 'חודש /' : '/ month'}
                    </span>
                    <span className="text-3xl md:text-4xl lg:text-5xl font-bold text-vibrant-orange font-hebrew">
                      <MixedText>{`₪${plan.price_ils}`}</MixedText>
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Features */}
            <div className="space-y-2 md:space-y-3 lg:space-y-4 mb-4 md:mb-6 lg:mb-8">
              {plan.features.map((feature: string, index: number) => (
                <div key={index} className={`flex items-center justify-between py-3 ${index < plan.features.length - 1 ? 'border-b border-surface-muted/10' : ''} ${isPremium ? 'border-vibrant-orange/10' : ''}`}>
                  {isRTL ? (
                    <>
                      <div className={`text-sm font-bold ${isPremium ? 'text-vibrant-orange' : 'text-surface-secondary'}`}>
                        <MixedText>{feature.split(':')[1]?.trim() || feature}</MixedText>
                      </div>
                      <h4 className="font-medium text-surface-primary font-hebrew text-right">
                        <MixedText>{feature.split(':')[0]?.trim() || feature}</MixedText>
                      </h4>
                    </>
                  ) : (
                    <>
                      <h4 className="font-medium text-surface-primary font-hebrew">
                        <MixedText>{feature.split(':')[0]?.trim() || feature}</MixedText>
                      </h4>
                      <div className={`text-sm font-bold ${isPremium ? 'text-vibrant-orange' : 'text-surface-secondary'}`}>
                        <MixedText>{feature.split(':')[1]?.trim() || feature}</MixedText>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <Button
              variant={isPremium ? "primary" : "secondary"}
              size="lg"
              className={`w-full font-hebrew ${isPremium ? 'group relative overflow-hidden' : ''}`}
              style={isPremium ? {
                background: 'linear-gradient(135deg, #F76700, #F76700DD)',
                boxShadow: '0 8px 24px rgba(247, 103, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
              } : {}}
              onClick={() => isPremium ? handleSubscriptionPurchase('premium') : undefined}
              disabled={processingPlan !== null || isCurrent}
            >
              {isPremium && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transition-opacity duration-300 transform -skew-x-12" />
              )}
              
              <div className="relative flex items-center justify-center space-x-2">
                {processingPlan === plan.id ? (
                  <LoadingSpinner size="sm" className="text-white" />
                ) : (
                  <>
                    {!isRTL && !isPremium && <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-200" />}
                    {isRTL && isPremium && <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-200" />}
                    <span className="text-lg">
                      <MixedText>
                        {isCurrent 
                          ? (isRTL ? 'התכנית הנוכחית' : 'Current Plan')
                          : isPremium 
                            ? (isRTL ? `התחל עכשיו - ₪${plan.price_ils}/חודש` : `Start Now - ₪${plan.price_ils}/month`)
                            : (isRTL ? 'התחל חינם' : 'Start Free')
                        }
                      </MixedText>
                    </span>
                    {!isRTL && isPremium && <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />}
                    {isRTL && !isPremium && <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200 rotate-180" />}
                  </>
                )}
              </div>
            </Button>
          </div>
        </SpaceCard>
      </div>
    );
  };


  return (
    <div className="fixed inset-0 flex items-center justify-center z-[60] p-4 pt-20" 
         style={{ background: 'rgba(26, 15, 43, 0.8)' }}>
      {/* Space Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gradient-to-br from-vibrant-orange/10 to-muted-purple/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/3 right-1/4 w-24 h-24 bg-gradient-to-br from-muted-purple/10 to-vibrant-orange/10 rounded-full blur-2xl animate-pulse delay-700"></div>
        <div className="absolute bottom-1/4 left-1/3 w-20 h-20 bg-gradient-to-br from-vibrant-orange/5 to-galaxy-pink/10 rounded-full blur-xl animate-pulse delay-1000"></div>
      </div>
      
      <SpaceCard 
        constellation="cassiopeia" 
        className="relative w-full max-w-5xl max-h-[80vh] overflow-y-auto shadow-2xl"
      >
        {/* Header */}
        <div className="relative">
          {/* Close Button */}
          <button
            onClick={onClose}
            className={`absolute top-6 z-10 p-2 rounded-lg text-surface-secondary hover:text-vibrant-orange hover:bg-vibrant-orange/10 transition-all duration-200 ${isRTL ? 'left-6' : 'right-6'}`}
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Header Content */}
          <div className="text-center py-4 md:py-6 lg:py-8 px-4 md:px-6">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-surface-primary font-hebrew mb-2 md:mb-3">
              <MixedText>{isRTL ? 'תמחור' : 'Pricing'}</MixedText>
            </h2>
            <div className="max-w-md mx-auto">
              <div className="bg-gradient-to-r from-vibrant-orange/10 to-muted-purple/10 rounded-xl p-2 md:p-3 lg:p-4 border border-vibrant-orange/20">
                <div className="text-vibrant-orange font-medium font-hebrew text-xs md:text-sm lg:text-base">
                  <MixedText>{isRTL ? 'פחות מעלות של שעת שיעור פרטי אחת' : 'Less than the cost of 1 hour private tutoring'}</MixedText>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content - Dynamic Plan Rendering */}
        <div className="px-4 md:px-6 pb-4 md:pb-6 lg:pb-8">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto ${isRTL ? 'lg:grid-flow-col-dense' : ''}`}>
            {/* Render plans dynamically based on RTL */}
            {isRTL ? (
              // RTL: Premium first (left), then Free
              <>
                {premiumPlan && <PlanCard plan={premiumPlan} className="lg:order-1" />}
                {freePlan && <PlanCard plan={freePlan} className="lg:order-2" />}
              </>
            ) : (
              // LTR: Free first (left), then Premium
              <>
                {freePlan && <PlanCard plan={freePlan} />}
                {premiumPlan && <PlanCard plan={premiumPlan} />}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 md:px-6 pb-4 md:pb-6">
          <div className="text-center p-2 md:p-3 lg:p-4 bg-surface-muted/5 rounded-lg border border-surface-muted/10">
            <p className="text-xs md:text-sm text-surface-muted font-hebrew">
              <MixedText>{isRTL 
                ? 'ניתן לביטול בכל עת • תשלום מאובטח בכרטיס אשראי • ללא התחייבות'
                : 'Cancel anytime • Secure credit card payment • No commitment'
              }</MixedText>
            </p>
          </div>
        </div>
      </SpaceCard>
    </div>
  );
};

export default UpgradeModal;